import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithMutation } from 'jotai-tanstack-query';

import { postChannelVerificationReportApi } from '@flipster/apis/v1/channel-verification';
import { type ChannelTypeValue } from '@flipster/apis/v1/channel-verification/types';
import { T } from '@flipster/i18n';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { RouterScope } from '@flipster/react/providers/route';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorMessage } from '@flipster/utils/error';

import { channelValues } from '../../constants';

export const descriptionMaxLength = 250;

const validChannels = new Set<string>(channelValues);

export const channelVerificationReportPageBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const router = bunja.use(RouterScope);

  // Form state
  const channelAtom = atom<ChannelTypeValue | null>(null);
  const sourceAtom = atom('');
  const descriptionAtom = atom('');

  // Derived
  const isValidAtom = atom((get) => {
    const channel = get(channelAtom);
    const source = get(sourceAtom).trim();
    const description = get(descriptionAtom);
    return channel !== null && source.length > 0 && description.length <= descriptionMaxLength;
  });

  // Report mutation
  const reportMutationAtom = atomWithMutation(() => ({
    mutationKey: ['postChannelVerificationReportApi'],
    mutationFn: postChannelVerificationReportApi,
  }));
  const isReportingAtom = atom((get) => get(reportMutationAtom).isPending);

  async function submit() {
    const isValid = store.get(isValidAtom);
    if (!isValid) return;

    const channel = store.get(channelAtom);
    const source = store.get(sourceAtom).trim();
    const description = store.get(descriptionAtom).trim();
    if (!channel) return;

    try {
      await store.get(reportMutationAtom).mutateAsync({
        channel,
        source,
        description: description || null,
      });
      snackbar.success({ message: <T id='VIP.r260316.Report_submitted_successfully' /> });
      router.replace('/channel-verification');
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
    }
  }

  // Hydrate from search params
  bunja.effect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const channelParam = params.get('channel');
    if (channelParam && validChannels.has(channelParam)) {
      store.set(channelAtom, channelParam as ChannelTypeValue);
    }
    const source = params.get('source');
    if (source) store.set(sourceAtom, source);
  });

  return {
    channelAtom,
    sourceAtom,
    descriptionAtom,
    isValidAtom,
    isReportingAtom,
    submit,
  };
});
channelVerificationReportPageBunja.debugLabel = 'channelVerificationReportPageBunja';
